import type {
  InstantWinCountersUpdateEvent,
  InstantWinDraw,
  InstantWinResult,
  Paginated,
} from '@/types'
import { mapToDateTime } from '@/utils/date-time-utils'
import type {
  InstantGameServiceInterface,
  InstantGameWinnerOptions,
} from '@core/vue-plugins/instant-games/types'

import type ApiClient from '../lib/ApiClient'
import type {
  GameCountersResponse,
  WinnersResponse,
} from '../lib/ApiClient.types'
import { createLogger } from '../lib/log'

const { log } = createLogger('InstantGameService')

export default class InstantGameService implements InstantGameServiceInterface {
  constructor(private apiClient: ApiClient) {}

  async getGames() {
    log('getGames()')
    const dto = await this.apiClient.get<InstantWinDraw[]>('/games')
    return mapToDateTime(dto)
  }

  async getGameById(id: string) {
    log('getGameById()', id)
    const dto = await this.apiClient.get<InstantWinDraw>(`/games/${id}`)
    return mapToDateTime(dto)
  }

  async getWinners(options: InstantGameWinnerOptions) {
    log('getWinners()', options)
    const dto = await this.apiClient.get<WinnersResponse>('/winners', {
      query: {
        current_page: options.page,
        currency_threshold: options.currencyThreshold,
        unique_per_customer: options.uniquePerCustomer ? '1' : '0',
      },
    })
    return this.winnersFromDto(dto)
  }

  async getGameCounters(id: string) {
    log('getGameCounters()', id)
    return (await this.apiClient.get<GameCountersResponse>(
      `/games/${id}/counters`
    )) as InstantWinCountersUpdateEvent
  }

  /**
   * Converts a JSON payload from the API into a Winner object. Replacing dates
   * with DateTime instances.
   *
   * @param dto The Data Transfer Object to convert from
   * @returns Converted object
   */
  private winnersFromDto = (
    dto: WinnersResponse
  ): Paginated<InstantWinResult> => {
    const { items, ...rest } = dto
    return {
      data: mapToDateTime(items) as InstantWinResult[],
      next_page_url: '',
      ...rest,
    }
  }
}
